import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { AlertTriangle, Boxes, MapPin, PackageCheck, PoundSterling, TrendingUp } from 'lucide-react';
import {
  getEquipmentBySite,
  getCostSummary,
  getAssetAgeWarranty,
  getAssetQuality,
  getManagedReturns,
  getProfitAndLoss,
  type ProfitByItem,
} from '../../api/reporting';
import { returnAsset } from '../../api/assets';
import { apiErrorMessage } from '../../lib/api';
import { useToast } from '../../components/ui/Toast';
import { Card, CardBody, CardHeader } from '../../components/ui/Card';
import { Table, Thead, Th, Tr, Td } from '../../components/ui/Table';
import { Badge } from '../../components/ui/Badge';
import { Button } from '../../components/ui/Button';
import { ConfirmModal } from '../../components/ui/ConfirmModal';
import { Spinner } from '../../components/ui/Spinner';
import { EmptyState } from '../../components/ui/EmptyState';
import { ReportExportBar } from '../../components/ReportExportBar';
import type {
  AssetAgeWarrantyRow,
  AssetQualityRow,
  EquipmentBySiteRow,
  ManagedReturnRow,
  WarrantyStatus,
} from '../../types/api';

function money(value: number) {
  return `£${value.toFixed(2)}`;
}

function StatCard({ icon: Icon, label, value }: { icon: typeof Boxes; label: string; value: string }) {
  return (
    <Card>
      <CardBody className="flex items-center gap-3">
        <div className="flex h-9 w-9 shrink-0 items-center justify-center rounded-lg bg-brand-50 text-brand-600">
          <Icon className="h-4 w-4" />
        </div>
        <div>
          <p className="text-xs font-medium uppercase tracking-wide text-slate-500">{label}</p>
          <p className="mt-0.5 text-lg font-semibold text-slate-900">{value}</p>
        </div>
      </CardBody>
    </Card>
  );
}

const WARRANTY_TONE: Record<WarrantyStatus, 'green' | 'amber' | 'red' | 'slate'> = {
  VALID: 'green',
  EXPIRING_SOON: 'amber',
  EXPIRED: 'red',
  UNKNOWN: 'slate',
};

function ManagedReturnsSection({ orgId, canManage, orgName }: { orgId: string; canManage: boolean; orgName?: string }) {
  const queryClient = useQueryClient();
  const { showToast } = useToast();
  const [returnTarget, setReturnTarget] = useState<ManagedReturnRow | null>(null);
  const [error, setError] = useState<string | null>(null);

  const query = useQuery({ queryKey: ['managed-returns', orgId], queryFn: () => getManagedReturns(orgId) });

  const mutation = useMutation({
    mutationFn: () => returnAsset(returnTarget!.assetId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['managed-returns', orgId] });
      showToast(`${returnTarget?.itemName} marked as returned - it'll drop off this customer's next invoice.`);
      setReturnTarget(null);
    },
    onError: (err) => setError(apiErrorMessage(err, 'Could not mark this asset as returned.')),
  });

  return (
    <div>
      <div className="mb-2 flex items-center justify-between">
        <h2 className="text-xs font-semibold uppercase tracking-wide text-slate-500">Managed items due back</h2>
        <ReportExportBar
          filename={`managed-returns-${orgId}`}
          title="Managed items due back"
          subtitle={orgName}
          rows={query.data?.items ?? []}
          columns={[
            { header: 'Item', value: (r: ManagedReturnRow) => r.itemName },
            { header: 'Serial', value: (r: ManagedReturnRow) => r.serialNumber ?? '' },
            { header: 'Site', value: (r: ManagedReturnRow) => r.siteName },
            { header: 'Term end', value: (r: ManagedReturnRow) => new Date(r.termEndDate).toLocaleDateString() },
            {
              header: 'Status',
              value: (r: ManagedReturnRow) =>
                r.dueStatus === 'OVERDUE' ? `${Math.abs(r.daysUntilDue)} days overdue` : `Due in ${r.daysUntilDue} days`,
            },
          ]}
        />
      </div>
      <Card>
        {query.isLoading ? (
          <div className="py-14 text-center">
            <Spinner />
          </div>
        ) : !query.data || query.data.items.length === 0 ? (
          <EmptyState icon={PackageCheck} title="Nothing due back" description="No managed items are overdue or coming up for return." />
        ) : (
          <>
            <CardHeader>
              <div className="flex flex-wrap gap-2">
                <Badge tone="red">Overdue: {query.data.summary.overdue}</Badge>
                <Badge tone="amber">Due within {query.data.dueSoonDays} days: {query.data.summary.dueSoon}</Badge>
              </div>
            </CardHeader>
            <Table>
              <Thead>
                <tr>
                  <Th>Item</Th>
                  <Th>Serial</Th>
                  <Th>Site</Th>
                  <Th>Term end</Th>
                  <Th>Status</Th>
                  {canManage && <Th></Th>}
                </tr>
              </Thead>
              <tbody>
                {query.data.items.map((row) => (
                  <Tr key={row.assetId}>
                    <Td className="font-medium text-slate-900">{row.itemName}</Td>
                    <Td>{row.serialNumber ?? '-'}</Td>
                    <Td>{row.siteName}</Td>
                    <Td>{new Date(row.termEndDate).toLocaleDateString()}</Td>
                    <Td>
                      <Badge tone={row.dueStatus === 'OVERDUE' ? 'red' : 'amber'}>
                        {row.dueStatus === 'OVERDUE' ? `${Math.abs(row.daysUntilDue)} days overdue` : `Due in ${row.daysUntilDue} days`}
                      </Badge>
                    </Td>
                    {canManage && (
                      <Td className="text-right">
                        <Button
                          size="sm"
                          variant="secondary"
                          onClick={() => {
                            setError(null);
                            setReturnTarget(row);
                          }}
                        >
                          Mark returned
                        </Button>
                      </Td>
                    )}
                  </Tr>
                ))}
              </tbody>
            </Table>
          </>
        )}
      </Card>

      <ConfirmModal
        open={!!returnTarget}
        onClose={() => setReturnTarget(null)}
        onConfirm={() => mutation.mutate()}
        title="Mark item as returned"
        description={`Confirms ${returnTarget?.itemName}${returnTarget?.serialNumber ? ` (${returnTarget.serialNumber})` : ''} has been collected back in. It'll be taken off this customer's regular invoice from now on.`}
        confirmLabel="Mark returned"
        tone="primary"
        isLoading={mutation.isPending}
        error={error}
      />
    </div>
  );
}

// Oakford's cost basis and margin on this one customer - never shown to
// Customer Admin/Reports Viewer, only rendered when isMspAdmin is true (the
// parent CustomerDetailPage passes this straight from useAuth(), not
// derived from canManage - canManage also covers Customer Admin, whereas
// this section must not).
function ProfitLossSection({ orgId, orgName }: { orgId: string; orgName?: string }) {
  const query = useQuery({ queryKey: ['profit-loss', orgId], queryFn: () => getProfitAndLoss(orgId) });

  return (
    <div>
      <div className="mb-2 flex items-center justify-between">
        <h2 className="text-xs font-semibold uppercase tracking-wide text-slate-500">Profit &amp; loss (internal)</h2>
        <ReportExportBar
          filename={`profit-loss-${orgId}`}
          title="Profit & loss"
          subtitle={orgName}
          rows={query.data?.byItem ?? []}
          columns={[
            { header: 'Item', value: (r: ProfitByItem) => r.name },
            { header: 'Revenue (£)', value: (r: ProfitByItem) => r.revenue.toFixed(2) },
            { header: 'Cost (£)', value: (r: ProfitByItem) => r.cost.toFixed(2) },
            { header: 'Profit (£)', value: (r: ProfitByItem) => r.profit.toFixed(2) },
          ]}
        />
      </div>
      {query.isLoading ? (
        <Spinner />
      ) : query.data ? (
        <>
          <div className="mb-1 grid grid-cols-2 gap-4 sm:grid-cols-5">
            <StatCard icon={PoundSterling} label="Revenue so far" value={money(query.data.revenueSoFar)} />
            <StatCard icon={PoundSterling} label="Revenue total" value={money(query.data.revenueTotal)} />
            <StatCard icon={PoundSterling} label="Cost" value={money(query.data.cost)} />
            <StatCard icon={TrendingUp} label="Profit" value={money(query.data.profit)} />
            <StatCard icon={TrendingUp} label="Margin" value={`${query.data.marginPercent.toFixed(1)}%`} />
          </div>
          <p className="mb-4 text-xs text-slate-400">
            Revenue so far is what's actually been invoiced. Revenue total adds what's still to come from active
            managed items and contracts. Cost, profit and margin are based on revenue so far only.
          </p>
          {query.data.missingCostLineCount > 0 && (
            <div className="mb-4">
              <Badge tone="amber">
                {query.data.missingCostLineCount} invoiced line(s) ({money(query.data.missingCostRevenue)} revenue) have no
                cost basis set on their master catalog item - profit is understated until a default cost is filled in.
              </Badge>
            </div>
          )}
          <Card>
            {query.data.byItem.length === 0 ? (
              <EmptyState icon={PoundSterling} title="Nothing invoiced yet" />
            ) : (
              <Table>
                <Thead>
                  <tr>
                    <Th>Item</Th>
                    <Th>Revenue</Th>
                    <Th>Cost</Th>
                    <Th>Profit</Th>
                  </tr>
                </Thead>
                <tbody>
                  {query.data.byItem.map((row) => (
                    <Tr key={row.name}>
                      <Td className="font-medium text-slate-900">{row.name}</Td>
                      <Td>{money(row.revenue)}</Td>
                      <Td>{money(row.cost)}</Td>
                      <Td className={row.profit < 0 ? 'text-red-600' : ''}>{money(row.profit)}</Td>
                    </Tr>
                  ))}
                </tbody>
              </Table>
            )}
          </Card>
        </>
      ) : null}
    </div>
  );
}

export function ReportsTab({
  orgId,
  canManage,
  orgName,
  isMspAdmin,
}: {
  orgId: string;
  canManage: boolean;
  orgName?: string;
  isMspAdmin?: boolean;
}) {
  const costQuery = useQuery({ queryKey: ['cost-summary', orgId], queryFn: () => getCostSummary(orgId) });
  const equipmentQuery = useQuery({ queryKey: ['equipment-by-site', orgId], queryFn: () => getEquipmentBySite(orgId) });
  const ageQuery = useQuery({ queryKey: ['asset-age-warranty', orgId], queryFn: () => getAssetAgeWarranty(orgId) });
  const qualityQuery = useQuery({ queryKey: ['asset-quality', orgId], queryFn: () => getAssetQuality(orgId) });

  return (
    <div className="flex flex-col gap-6">
      <ManagedReturnsSection orgId={orgId} canManage={canManage} orgName={orgName} />

      {isMspAdmin && <ProfitLossSection orgId={orgId} orgName={orgName} />}

      <div>
        <div className="mb-2 flex items-center justify-between">
          <h2 className="text-xs font-semibold uppercase tracking-wide text-slate-500">Cost summary</h2>
          <ReportExportBar
            filename={`cost-summary-${orgId}`}
            title="Cost summary"
            subtitle={orgName}
            rows={costQuery.data ? [
              { label: 'Billed to date', value: costQuery.data.historicalTotal },
              { label: 'One-off billed', value: costQuery.data.historicalOneOff },
              { label: 'Managed billed', value: costQuery.data.historicalManaged },
              { label: 'Pending one-off', value: costQuery.data.pendingOneOff },
              { label: 'Forecast remaining', value: costQuery.data.forecastRemainingManaged },
            ] : []}
            columns={[
              { header: 'Metric', value: (r: { label: string; value: number }) => r.label },
              { header: 'Amount (£)', value: (r: { label: string; value: number }) => r.value.toFixed(2) },
            ]}
          />
        </div>
        {costQuery.isLoading ? (
          <Spinner />
        ) : costQuery.data ? (
          <div className="grid grid-cols-2 gap-4 sm:grid-cols-3 lg:grid-cols-5">
            <StatCard icon={PoundSterling} label="Billed to date" value={money(costQuery.data.historicalTotal)} />
            <StatCard icon={PoundSterling} label="One-off billed" value={money(costQuery.data.historicalOneOff)} />
            <StatCard icon={PoundSterling} label="Managed billed" value={money(costQuery.data.historicalManaged)} />
            <StatCard icon={AlertTriangle} label="Pending one-off" value={money(costQuery.data.pendingOneOff)} />
            <StatCard icon={TrendingUp} label="Forecast remaining" value={money(costQuery.data.forecastRemainingManaged)} />
          </div>
        ) : null}
      </div>

      <div>
        <div className="mb-2 flex items-center justify-between">
          <h2 className="text-xs font-semibold uppercase tracking-wide text-slate-500">Equipment by site</h2>
          <ReportExportBar
            filename={`equipment-by-site-${orgId}`}
            title="Equipment by site"
            subtitle={orgName}
            rows={equipmentQuery.data ?? []}
            columns={[
              { header: 'Site', value: (r: EquipmentBySiteRow) => r.siteName },
              { header: 'Lines', value: (r: EquipmentBySiteRow) => r.lineCount },
              { header: 'Quantity', value: (r: EquipmentBySiteRow) => r.totalQuantity },
              { header: 'Value (£)', value: (r: EquipmentBySiteRow) => r.totalValue.toFixed(2) },
            ]}
          />
        </div>
        <Card>
          {equipmentQuery.isLoading ? (
            <div className="py-14 text-center">
              <Spinner />
            </div>
          ) : !equipmentQuery.data || equipmentQuery.data.length === 0 ? (
            <EmptyState icon={MapPin} title="No committed equipment yet" />
          ) : (
            <Table>
              <Thead>
                <tr>
                  <Th>Site</Th>
                  <Th>Lines</Th>
                  <Th>Quantity</Th>
                  <Th>Value</Th>
                </tr>
              </Thead>
              <tbody>
                {equipmentQuery.data.map((row) => (
                  <Tr key={row.siteId}>
                    <Td className="font-medium text-slate-900">{row.siteName}</Td>
                    <Td>{row.lineCount}</Td>
                    <Td>{row.totalQuantity}</Td>
                    <Td>{money(row.totalValue)}</Td>
                  </Tr>
                ))}
              </tbody>
            </Table>
          )}
        </Card>
      </div>

      <div>
        <div className="mb-2 flex items-center justify-between">
          <h2 className="text-xs font-semibold uppercase tracking-wide text-slate-500">Asset age &amp; warranty</h2>
          <ReportExportBar
            filename={`asset-age-warranty-${orgId}`}
            title="Asset age & warranty"
            subtitle={orgName}
            rows={ageQuery.data?.assets ?? []}
            columns={[
              { header: 'Item', value: (r: AssetAgeWarrantyRow) => r.itemName },
              { header: 'Serial', value: (r: AssetAgeWarrantyRow) => r.serialNumber ?? '' },
              { header: 'Site', value: (r: AssetAgeWarrantyRow) => r.siteName },
              { header: 'Age (days)', value: (r: AssetAgeWarrantyRow) => r.ageDays },
              { header: 'Warranty', value: (r: AssetAgeWarrantyRow) => r.warrantyStatus.replace('_', ' ') },
            ]}
          />
        </div>
        <Card>
          {ageQuery.isLoading ? (
            <div className="py-14 text-center">
              <Spinner />
            </div>
          ) : !ageQuery.data || ageQuery.data.assets.length === 0 ? (
            <EmptyState icon={Boxes} title="No assets yet" />
          ) : (
            <>
              <CardHeader>
                <div className="flex flex-wrap gap-2">
                  <Badge tone="green">Valid: {ageQuery.data.summary.byWarrantyStatus.VALID}</Badge>
                  <Badge tone="amber">Expiring soon: {ageQuery.data.summary.byWarrantyStatus.EXPIRING_SOON}</Badge>
                  <Badge tone="red">Expired: {ageQuery.data.summary.byWarrantyStatus.EXPIRED}</Badge>
                  <Badge>Unknown: {ageQuery.data.summary.byWarrantyStatus.UNKNOWN}</Badge>
                  <span className="text-xs text-slate-400">
                    Avg age {ageQuery.data.summary.averageAgeDays} days &middot; {ageQuery.data.summary.totalAssets} asset(s)
                  </span>
                </div>
              </CardHeader>
              <Table>
                <Thead>
                  <tr>
                    <Th>Item</Th>
                    <Th>Serial</Th>
                    <Th>Site</Th>
                    <Th>Age (days)</Th>
                    <Th>Warranty</Th>
                  </tr>
                </Thead>
                <tbody>
                  {ageQuery.data.assets.map((row) => (
                    <Tr key={row.assetId}>
                      <Td className="font-medium text-slate-900">{row.itemName}</Td>
                      <Td>{row.serialNumber ?? '-'}</Td>
                      <Td>{row.siteName}</Td>
                      <Td>{row.ageDays}</Td>
                      <Td>
                        <Badge tone={WARRANTY_TONE[row.warrantyStatus]}>{row.warrantyStatus.replace('_', ' ')}</Badge>
                      </Td>
                    </Tr>
                  ))}
                </tbody>
              </Table>
            </>
          )}
        </Card>
      </div>

      <div>
        <div className="mb-2 flex items-center justify-between">
          <h2 className="text-xs font-semibold uppercase tracking-wide text-slate-500">Asset quality</h2>
          <ReportExportBar
            filename={`asset-quality-${orgId}`}
            title="Asset quality"
            subtitle={orgName}
            rows={qualityQuery.data?.assets ?? []}
            columns={[
              { header: 'Item', value: (r: AssetQualityRow) => r.itemName },
              { header: 'Serial', value: (r: AssetQualityRow) => r.serialNumber ?? '' },
              { header: 'Status', value: (r: AssetQualityRow) => r.status.replace('_', ' ') },
              { header: 'Condition', value: (r: AssetQualityRow) => r.condition ?? '' },
              { header: 'Events', value: (r: AssetQualityRow) => r.eventCount },
              { header: 'Last event', value: (r: AssetQualityRow) => (r.lastEventType ? r.lastEventType.replace('_', ' ') : '') },
            ]}
          />
        </div>
        <Card>
          {qualityQuery.isLoading ? (
            <div className="py-14 text-center">
              <Spinner />
            </div>
          ) : !qualityQuery.data || qualityQuery.data.assets.length === 0 ? (
            <EmptyState icon={Boxes} title="No assets yet" />
          ) : (
            <>
              <CardHeader>
                <div className="flex flex-wrap gap-2 text-xs text-slate-500">
                  {Object.entries(qualityQuery.data.eventCountsByType).map(([type, count]) => (
                    <Badge key={type}>
                      {type.replace('_', ' ')}: {count}
                    </Badge>
                  ))}
                </div>
              </CardHeader>
              <Table>
                <Thead>
                  <tr>
                    <Th>Item</Th>
                    <Th>Serial</Th>
                    <Th>Status</Th>
                    <Th>Condition</Th>
                    <Th>Events</Th>
                    <Th>Last event</Th>
                  </tr>
                </Thead>
                <tbody>
                  {qualityQuery.data.assets.map((row) => (
                    <Tr key={row.assetId}>
                      <Td className="font-medium text-slate-900">{row.itemName}</Td>
                      <Td>{row.serialNumber ?? '-'}</Td>
                      <Td>{row.status.replace('_', ' ')}</Td>
                      <Td>{row.condition ?? '-'}</Td>
                      <Td>{row.eventCount}</Td>
                      <Td>{row.lastEventType ? row.lastEventType.replace('_', ' ') : '-'}</Td>
                    </Tr>
                  ))}
                </tbody>
              </Table>
            </>
          )}
        </Card>
      </div>
    </div>
  );
}
